import { apiClient } from './api';
import { API_BASE_URL as API_BASE } from '../config/apiBase';
import type { SymbolData, ExportType, CopyType } from '../types/marketData';

interface ComboLatestResponse {
  data: SymbolData[] | { data: SymbolData[] };
}

export const marketDataService = {
  /**
   * Fetch combo data for given symbols.
   *
   * Routed through apiClient (not raw fetch) so the Authorization token is
   * attached: combo/latest now requires authentication (steamroom-gated). The
   * export helpers below stay on raw fetch because those endpoints remain
   * public for external integrations (TradingView, TOS).
   */
  async fetchComboData(symbols: string[]): Promise<SymbolData[]> {
    const result = await apiClient.get<ComboLatestResponse>(
      '/api/market-data/combo/latest/',
      { symbols: symbols.join(',') }
    );

    // Handle nested response structure: result.data.data
    const payload = result.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    return payload.data;
  },

  /**
   * Get export URL for downloading files
   */
  getExportUrl(type: ExportType, symbols: string[]): string {
    const endpoints = {
      'csv': 'combo/export',
      'tos': 'combo/export-tos',
      'tradingview': 'combo/export-tradingview',
      'tradingview-v2': 'combo/export-tradingview-v2',
    };
    return `${API_BASE}/api/market-data/${endpoints[type]}/?symbols=${symbols.join(',')}`;
  },

  /**
   * Copy export data to clipboard
   */
  async copyToClipboard(type: CopyType, symbols: string[]): Promise<void> {
    const url = this.getExportUrl(type, symbols);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch ${type} data: ${response.statusText}`);
    }

    const text = await response.text();
    await navigator.clipboard.writeText(text);
  },

  /**
   * Download export file
   */
  downloadExport(type: ExportType, symbols: string[]): void {
    const url = this.getExportUrl(type, symbols);
    window.location.href = url;
  }
};
